import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star, MapPin } from "lucide-react";
import { Reveal, Overline, EASE } from "@/lib/Reveal";
import { TESTIMONIALS } from "@/lib/site";

export default function TestimonialsCarousel() {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);

    const go = useCallback((next, direction) => {
        setDir(direction);
        setIndex((next + TESTIMONIALS.length) % TESTIMONIALS.length);
    }, []);

    useEffect(() => {
        const t = setInterval(() => go(index + 1, 1), 7000);
        return () => clearInterval(t);
    }, [index, go]);

    const t = TESTIMONIALS[index];

    return (
        <section data-testid="testimonials-section" className="py-20 lg:py-28 bg-[#F3F3EF]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <Overline>Testimonials</Overline>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900 mt-4 leading-[1.05]">
                                What customers say
                            </h2>
                            <p className="mt-5 text-base text-neutral-600 leading-relaxed">
                                We are a growing local business. Genuine reviews from our Luton customers will be
                                featured here as they come in — the cards shown are clearly marked placeholders.
                            </p>
                            <div className="mt-8 flex items-center gap-3">
                                <button
                                    data-testid="testimonial-nav-prev-button"
                                    onClick={() => go(index - 1, -1)}
                                    className="w-12 h-12 rounded-full border-2 border-neutral-900 text-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-colors duration-200"
                                    aria-label="Previous testimonial"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    data-testid="testimonial-nav-next-button"
                                    onClick={() => go(index + 1, 1)}
                                    className="w-12 h-12 rounded-full bg-[#E85A1D] text-white flex items-center justify-center hover:bg-[#D24B10] transition-colors duration-200"
                                    aria-label="Next testimonial"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="flex items-center gap-2 ml-3">
                                    {TESTIMONIALS.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => go(i, i > index ? 1 : -1)}
                                            aria-label={`Go to testimonial ${i + 1}`}
                                            className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                                                i === index ? "w-7 bg-[#E85A1D]" : "w-2 bg-neutral-300 hover:bg-neutral-400"
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-8 relative min-h-[320px]">
                        <AnimatePresence mode="wait" custom={dir}>
                            <motion.article
                                key={index}
                                data-testid="testimonial-card"
                                custom={dir}
                                initial={{ opacity: 0, x: 60 * dir }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -60 * dir }}
                                transition={{ duration: 0.5, ease: EASE }}
                                className="bg-white rounded-2xl border border-[#E5E5E0] shadow-[0_16px_50px_rgba(0,0,0,0.07)] p-8 sm:p-12 relative overflow-hidden"
                            >
                                <Quote className="absolute -top-4 -right-4 w-36 h-36 text-[#FFF1EB]" strokeWidth={1} />
                                <div className="relative">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <div className="flex gap-1" aria-label="5 star rating">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                                            ))}
                                        </div>
                                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] font-semibold bg-[#FFF1EB] text-[#E85A1D] px-3 py-1.5 rounded-full">
                                            Placeholder Review
                                        </span>
                                    </div>
                                    <p className="mt-7 font-display text-xl sm:text-2xl font-bold text-neutral-900 leading-snug tracking-tight">
                                        “{t.quote}”
                                    </p>
                                    <div className="mt-8 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-[#141414] flex items-center justify-center">
                                            <span className="font-display font-extrabold text-white text-lg">
                                                {t.name.charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-neutral-900 text-sm">
                                                {t.name} · {t.service}
                                            </p>
                                            <p className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
                                                <MapPin className="w-3.5 h-3.5 text-[#E85A1D]" />
                                                {t.location}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.article>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
